// services/bot-engine.ts
//
// Seeds a live session with believable players so a cold-start stream never looks
// empty and the host always has something to narrate. Bots are NOT a separate
// data path — they ride the exact human write paths (joinSession / submitAnswer),
// authored by the presenter's admin client which already has permission and
// already knows the answer key. So existing publishLeaderboard scores them
// unchanged, and the only thing the audience ever sees is names + scores on the
// top-N board (no participant fan-out). That makes the NAME POOL and the TIMING
// the only possible tells — both are deliberately human here.
//
// Reads (quiz blob, answer key) happen in the control surface and are handed in;
// this module only ever writes, so it never opens a second subscription.
import { Quiz, SessionState } from '../types/quiz.types';
import { joinSession, submitAnswer, serverNow } from './live-session';
import { AVATARS } from '../constants/avatars';

export type Tier = 'sharp' | 'average' | 'casual';

export interface Bot {
  uid: string;
  name: string;
  // Most (not all) bots pick a face, like real players do — keeps a seeded
  // lobby indistinguishable from a human one.
  avatar?: string;
  tier: Tier;
}

// What the host's narration panel renders per bot. `correct` is host-only truth
// (the audience never learns right/wrong before the reveal).
export interface BotStatus {
  uid: string;
  name: string;
  tier: Tier;
  state: 'pending_join' | 'present' | 'thinking' | 'answered';
  correct?: boolean;
  answeredAtMs?: number; // ms into the question window, for the feed ordering
}

interface TierProfile {
  pCorrect: number; // chance the chosen option is the right one
  pAnswer: number; // chance the bot answers at all (humans drop / go idle)
  // think window as a fraction of the question timer [min, max]
  thinkFrac: [number, number];
}

const TIERS: Record<Tier, TierProfile> = {
  // Sharp: usually right, answers fast and almost always shows up.
  sharp: { pCorrect: 0.9, pAnswer: 0.97, thinkFrac: [0.08, 0.55] },
  // Average: coin-flip-plus, mid-window, occasional no-show.
  average: { pCorrect: 0.6, pAnswer: 0.92, thinkFrac: [0.25, 0.8] },
  // Casual: often wrong, slow, sometimes never answers — the realism ballast.
  casual: { pCorrect: 0.33, pAnswer: 0.82, thinkFrac: [0.4, 0.92] },
};

// --- name pool --------------------------------------------------------------
// Plausible player handles for the target audience. Kept varied (plain first
// name / first + last initial / a few with number suffixes) so the leaderboard
// reads like a real Kahoot lobby, not a generated list. No "Bot N" anywhere.
const FIRST_NAMES = [
  'Aarav', 'Priya', 'Rohan', 'Sneha', 'Vikram', 'Ananya', 'Karan', 'Ishita',
  'Arjun', 'Megha', 'Rahul', 'Pooja', 'Aditya', 'Neha', 'Siddharth', 'Riya',
  'Aman', 'Diya', 'Kabir', 'Tanvi', 'Dev', 'Sara', 'Yash', 'Nisha', 'Harsh',
  'Kavya', 'Manish', 'Simran', 'Nikhil', 'Anjali', 'Varun', 'Shreya', 'Sahil',
  'Ayesha', 'Rajat', 'Divya', 'Akash', 'Trisha', 'Gaurav', 'Aditi', 'Vivek',
  'Mahi', 'Tushar', 'Ira', 'Naveen', 'Bhavna', 'Sameer', 'Lavanya', 'Imran',
  'Zoya',
];
const LAST_INITIALS = 'SKRMGPDVBNTJ'.split('');

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1));
const pick = <T>(arr: T[]) => arr[randInt(0, arr.length - 1)];

const styleName = (first: string): string => {
  const r = Math.random();
  if (r < 0.18) return `${first} ${pick(LAST_INITIALS)}.`; // "Rohan S."
  if (r < 0.3) return `${first}${randInt(1, 99)}`; // "Rohan42"
  return first; // plain, the common case
};

const newUid = () => `bot_${Math.random().toString(36).slice(2, 10)}`;

// Build a unique-named roster from per-tier counts.
export const generateRoster = (counts: Record<Tier, number>): Bot[] => {
  const used = new Set<string>();
  const names = [...FIRST_NAMES];
  // shuffle so repeated generations don't bias toward the front of the pool
  for (let i = names.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    [names[i], names[j]] = [names[j], names[i]];
  }
  let cursor = 0;
  const nextName = (): string => {
    for (let guard = 0; guard < 200; guard++) {
      const base = names[cursor % names.length];
      cursor++;
      const name = styleName(base);
      if (!used.has(name)) {
        used.add(name);
        return name;
      }
    }
    return `${pick(FIRST_NAMES)}${randInt(100, 999)}`; // last-resort uniqueness
  };

  const roster: Bot[] = [];
  (Object.keys(counts) as Tier[]).forEach((tier) => {
    for (let i = 0; i < counts[tier]; i++) {
      roster.push({
        uid: newUid(),
        name: nextName(),
        ...(Math.random() < 0.8 ? { avatar: pick(AVATARS) } : {}),
        tier,
      });
    }
  });
  return roster;
};

// --- runner -----------------------------------------------------------------
// Lifecycle: new BotRunner -> prime(quiz, correctAnswers) -> seedLobby()
// then feed it every session delta via onSession(). stop() clears timers;
// removeFromRtdb() cleans up seeded participants + answers for a clean re-run.
export class BotRunner {
  private statuses = new Map<string, BotStatus>();
  private timers: ReturnType<typeof setTimeout>[] = [];
  private quiz: Quiz | null = null;
  private correct: number[] = [];
  private joined = new Set<string>(); // uids whose participant row exists in RTDB
  private lastIndex = -1; // question the board was last reset for
  private windowWasOpen = false; // stream mode: tracks answersOpen edges
  private stopped = false;

  constructor(
    private readonly code: string,
    private readonly roster: Bot[],
    private readonly onUpdate: () => void
  ) {
    roster.forEach((b) =>
      this.statuses.set(b.uid, {
        uid: b.uid,
        name: b.name,
        tier: b.tier,
        state: 'pending_join',
      })
    );
  }

  prime(quiz: Quiz, correctAnswers: number[]) {
    this.quiz = quiz;
    this.correct = correctAnswers;
  }

  getStatuses(): BotStatus[] {
    return Array.from(this.statuses.values());
  }

  // Trickle bots into the lobby over a few seconds so the waiting-count climbs
  // like real arrivals instead of snapping to N at once.
  seedLobby() {
    const spread = Math.min(14000, 1500 + this.roster.length * 700);
    this.roster.forEach((bot) => {
      this.schedule(rand(200, spread), async () => {
        await joinSession(this.code, bot.uid, bot.name, bot.avatar);
        this.joined.add(bot.uid);
        this.set(bot.uid, { state: 'present' });
      });
    });
  }

  // Called on every session delta from the control surface's single subscription.
  //
  // App mode: schedule once per question, paced by the on-phone timer.
  // Host-paced sessions (stream mode, or app mode with delayOptions): the window
  // opens by hand, so bots answer ONLY while answersOpen — scheduled on the open
  // edge, cancelled on lock — exactly the constraint humans are under. Anything
  // else makes the rehearsal lie: answered-counts climbing while the pad is
  // locked, or "answers" landing after the reveal is on the broadcast.
  onSession(session: SessionState | null) {
    if (!session || this.stopped) return;
    if (session.status === 'ended') {
      this.clearTimers();
      return;
    }
    if (session.status !== 'in_progress') return;

    const hostPaced = !!session.youtubeUrl || !!session.delayOptions;
    const index = session.currentQuestionIndex;

    if (index !== this.lastIndex) {
      this.lastIndex = index;
      this.windowWasOpen = false;
      this.clearTimers();
      this.resetBoard();
      if (!hostPaced) this.scheduleAnswers(index, session.questionStartTime);
    }

    if (!hostPaced) return;
    const open = !!session.answersOpen;
    if (open && !this.windowWasOpen) {
      this.windowWasOpen = true;
      // openAnswers refreshed questionStartTime, so think-times measure from the
      // moment the window actually opened — not from the (earlier) advance.
      this.scheduleAnswers(index, session.questionStartTime);
    } else if (!open && this.windowWasOpen) {
      this.windowWasOpen = false;
      this.clearTimers(); // still-thinking bots just missed the window — human enough
      this.getStatuses().forEach((s) => {
        if (s.state === 'thinking') this.set(s.uid, { state: 'present' });
      });
    }
  }

  // New question: wipe last round's marks; everyone already in goes back to idle.
  private resetBoard() {
    this.roster.forEach((bot) => {
      const cur = this.statuses.get(bot.uid);
      if (cur && cur.state !== 'pending_join') {
        this.statuses.set(bot.uid, { ...cur, state: 'present', correct: undefined, answeredAtMs: undefined });
      }
    });
    this.onUpdate();
  }

  private scheduleAnswers(index: number, questionStartTime: number) {
    const q = this.quiz?.questions[index];
    if (!q) return;
    const window = (q.timer || 30) * 1000;
    const optionCount = q.options.length || 4;
    const correctIdx = this.correct[index] ?? -1;

    this.roster.forEach((bot) => {
      // Re-opened window (host retracted a reveal): whoever answered stays answered.
      if (this.statuses.get(bot.uid)?.state === 'answered') return;
      const prof = TIERS[bot.tier];
      if (Math.random() > prof.pAnswer) return; // this one sits this question out

      const willBeCorrect = correctIdx >= 0 && Math.random() < prof.pCorrect;
      const chosen = willBeCorrect ? correctIdx : wrongIndex(optionCount, correctIdx);

      // Human-like timing: a point inside this tier's think-window, jittered,
      // clamped to leave a small buffer before the timer expires.
      const [lo, hi] = prof.thinkFrac;
      const targetMs = Math.min(window - 700, rand(lo, hi) * window + rand(-400, 400));
      // questionStartTime is a SERVER timestamp — measure elapsed on the server
      // clock or every bot's think-time shifts by this device's clock skew.
      const elapsed = serverNow() - questionStartTime;
      const delay = Math.max(400, targetMs - elapsed);

      this.set(bot.uid, { state: 'thinking' });
      this.schedule(delay, async () => {
        try {
          // A question can start mid-lobby-trickle (clearTimers cancels pending
          // joins) — make sure the participant row exists before the first answer,
          // or the leaderboard shows this bot as a nameless "Player".
          if (!this.joined.has(bot.uid)) {
            await joinSession(this.code, bot.uid, bot.name, bot.avatar);
            this.joined.add(bot.uid);
          }
          await submitAnswer(this.code, bot.uid, index, chosen);
          this.set(bot.uid, {
            state: 'answered',
            correct: correctIdx >= 0 ? chosen === correctIdx : undefined,
            answeredAtMs: serverNow() - questionStartTime,
          });
        } catch {
          this.set(bot.uid, { state: 'present' }); // write failed — reads as a no-show
        }
      });
    });
  }

  stop() {
    this.stopped = true;
    this.clearTimers();
  }

  // Best-effort cleanup so the next rehearsal/run starts clean. Admin rules allow
  // writing null to these per-uid paths.
  async removeFromRtdb() {
    const { db } = await import('../firebase');
    const { ref, remove } = await import('firebase/database');
    await Promise.all(
      this.roster.flatMap((b) => [
        remove(ref(db, `quizDetails/${this.code}/participants/${b.uid}`)),
        remove(ref(db, `quizAnswers/${this.code}/${b.uid}`)),
      ])
    );
  }

  private set(uid: string, patch: Partial<BotStatus>) {
    const cur = this.statuses.get(uid);
    if (!cur) return;
    this.statuses.set(uid, { ...cur, ...patch });
    this.onUpdate();
  }

  private schedule(delay: number, fn: () => void | Promise<void>) {
    this.timers.push(
      setTimeout(() => {
        if (!this.stopped) void fn();
      }, delay)
    );
  }

  private clearTimers() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }
}

// Pick any option that isn't the correct one (uniformly). If there's no answer
// key (correctIdx < 0) just pick anything — it can't be scored as right anyway.
const wrongIndex = (optionCount: number, correctIdx: number): number => {
  if (optionCount <= 1) return 0;
  let i = randInt(0, optionCount - 1);
  if (correctIdx < 0) return i;
  while (i === correctIdx) i = randInt(0, optionCount - 1);
  return i;
};
